import isEqual from 'lodash/isEqual';
import type { ReactElement } from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type {
  InstantSearchProps,
  InstantSearchServerState,
} from 'react-instantsearch';
import {
  Configure,
  InstantSearch,
  InstantSearchSSRProvider,
} from 'react-instantsearch';

import { HitsPagination } from '@/components/HitsPagination';
import { InstantSearchStateUpdater } from '@/components/InstantSearchUpdater/InstantSearchUpdater';
import { useRouter } from '@/hooks/useRouter';
import {
  buildQueryParamsFromSearchUiState,
  buildUiStateFromURL,
  getIndexName,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { SearchUiState } from '@/integrations/algolia/types';
import type { ContentfulData } from '@/integrations/contentful';
import { Default } from '@/layouts/Default';
import { getRegionFromLocale } from '@/lib/regions';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { listingActions } from '@/states/listingState';

import { renderSections } from './cms';
import { Results } from './components/Boutiques/Results';
import { TopBanner } from './components/Boutiques/TopBanner';

export const HITSPERPAGE = 9;

interface BoutiqueListViewProps {
  cmsDataAboveBoutiqueList: ContentfulData[];
  cmsDataBelowBoutiqueList: ContentfulData[];
  facetFilters: (string | string[])[];
  instantSearchProps?: Partial<InstantSearchProps>;
  serverState?: InstantSearchServerState;
}

export const BoutiqueListView = ({
  cmsDataAboveBoutiqueList,
  cmsDataBelowBoutiqueList,
  facetFilters,
  instantSearchProps,
  serverState,
}: BoutiqueListViewProps): ReactElement => {
  const { asPath, locale, push, query } = useRouter();
  const indexName = getIndexName('STORE', locale);
  const lastUiState = useRef<SearchUiState | null>(null);
  const draftMode = useContext(DraftModeContext);
  const algoliaSearchClient = createSearchClient({
    isPreview: draftMode,
  });

  const uiStateFromURL = useMemo(() => {
    const region = getRegionFromLocale(locale);
    return buildUiStateFromURL(
      asPath,
      indexName,
      undefined,
      undefined,
      region.locale,
    );
  }, [asPath, indexName, locale]);

  const syncStateWithUrl = useCallback(
    (uiState: SearchUiState) => {
      const queryParams = buildQueryParamsFromSearchUiState(uiState, '', query);
      const pathWithoutQuery = asPath.split('?')[0];
      push({ pathname: pathWithoutQuery, query: queryParams }, undefined, {
        shallow: true,
      });
    },

    [push, query],
  );

  useEffect(() => {
    listingActions.updateListingType('STORE');
  }, []);

  return (
    <InstantSearchSSRProvider {...serverState}>
      <InstantSearch
        indexName={indexName}
        initialUiState={uiStateFromURL}
        insights={false}
        onStateChange={({ setUiState, uiState }) => {
          if (!isEqual(uiState, lastUiState.current)) {
            lastUiState.current = uiState;
            setUiState(uiState);
            syncStateWithUrl(uiState);
          }
        }}
        searchClient={algoliaSearchClient}
        {...instantSearchProps}
      >
        <InstantSearchStateUpdater
          key={asPath}
          expectedUiState={uiStateFromURL}
        />
        <Configure
          facetFilters={[...facetFilters, 'web_visible:true']}
          hitsPerPage={HITSPERPAGE}
        />
        {renderSections(cmsDataAboveBoutiqueList, draftMode)}
        <TopBanner />
        <Results />
        <HitsPagination />
        {renderSections(cmsDataBelowBoutiqueList, draftMode)}
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
};

BoutiqueListView.getLayout = (page: ReactElement) => (
  <Default
    cmsData={page.props.cmsDefaultLayoutData}
    data-test-id='BoutiqueListViewDefault'
  >
    <DraftModeContext.Provider value={page.props.draftMode}>
      {page}
    </DraftModeContext.Provider>
  </Default>
);
